import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl, uploadFile } from "@/utils";
import { Creator } from "@/entities/Creator";
import { Product } from "@/entities/Product";
import { Project } from "@/entities/Project";
import { Offering } from "@/entities/Offering";
import { User } from "@/entities/User";
import {
  LayoutDashboard, Package, Briefcase, Sparkles, TrendingUp,
  UploadCloud, UserCircle, PlusCircle, Pencil, Trash2, X,
  Check, ImagePlus, ArrowRight, AlertCircle, Star, ExternalLink,
  Youtube, MapPin, Instagram, Twitter, Eye
} from "lucide-react";

const CATEGORIES = ["Artist (Entertainment)", "Content Creator", "Craftsman (Build To Spec)", "Healer (Remedies; Protocol)", "Manifestor", "Motivator", "Soul Worker (Passion Service)", "Problem Solver", "Visionary"];
const PRODUCT_CATS = CATEGORIES;

// ─── Manage Products Tab ───────────────────────────────────────────────────
function ProductForm({ product, creatorId, creatorName, onSave, onClose }) {
  const [form, setForm] = useState(product
    ? { ...product, tags: (product.tags || []).join(", ") }
    : { name: "", description: "", price: "", original_price: "", category: PRODUCT_CATS[0], stock: 10, status: "active", is_featured: false, is_new: true, tags: "", images: [] }
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImage = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const { url } = await uploadFile(file, { isPublic: true });
    set("images", [...(form.images || []), url]);
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    const data = {
      ...form,
      price: parseFloat(form.price),
      original_price: form.original_price ? parseFloat(form.original_price) : null,
      stock: parseInt(form.stock),
      tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      creator_id: creatorId,
      creator_name: creatorName,
    };
    if (product?.id) await Product.update(product.id, data);
    else await Product.create(data);
    setSaving(false); onSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#e8e4de] px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-[#1a1a1a]">{product ? "Edit Product" : "Add Product"}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Images */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Images</label>
            <div className="flex flex-wrap gap-2">
              {(form.images || []).map((img, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-[#e8e4de]">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("images", form.images.filter((_, j) => j !== i))} className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ))}
              <label className="w-16 h-16 border-2 border-dashed border-[#e8e4de] rounded-lg flex items-center justify-center cursor-pointer hover:border-[#c8a96e]">
                {uploading ? <div className="w-4 h-4 border-2 border-[#c8a96e] border-t-transparent rounded-full animate-spin" /> : <ImagePlus className="w-5 h-5 text-gray-300" />}
                <input type="file" accept="image/*" className="hidden" onChange={handleImage} disabled={uploading} />
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Name *</label>
            <input required value={form.name} onChange={e => set("name", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description *</label>
            <textarea required value={form.description} onChange={e => set("description", e.target.value)} rows={3} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e] resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Price ($) *</label>
              <input required type="number" min="0" step="0.01" value={form.price} onChange={e => set("price", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Stock</label>
              <input type="number" min="0" value={form.stock} onChange={e => set("stock", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Category</label>
            <select value={form.category} onChange={e => set("category", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c8a96e]">
              {PRODUCT_CATS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tags (comma separated)</label>
            <input value={form.tags} onChange={e => set("tags", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="handmade, gift, limited" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
            <select value={form.status} onChange={e => set("status", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c8a96e]">
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="sold_out">Sold Out</option>
            </select>
          </div>
          <div className="flex gap-5">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input type="checkbox" checked={form.is_featured} onChange={e => set("is_featured", e.target.checked)} className="accent-[#c8a96e]" /> Featured
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input type="checkbox" checked={form.is_new} onChange={e => set("is_new", e.target.checked)} className="accent-[#c8a96e]" /> Mark as New
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-[#e8e4de] py-2.5 rounded-full text-sm font-medium hover:bg-[#f5f3ef]">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#1a1a1a] hover:bg-[#c8a96e] text-white py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? "Saving..." : product ? "Save Changes" : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Manage Profile Tab ────────────────────────────────────────────────────
function ProfileTab({ creator, onSaved }) {
  const [form, setForm] = useState({ ...creator });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleUpload = async (e, field, setUploading) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const { url } = await uploadFile(file, { isPublic: true });
    set(field, url); setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    await Creator.update(creator.id, form);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    onSaved(form);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2 text-green-700 text-sm">
          <Check className="w-4 h-4" /> Profile saved successfully!
        </div>
      )}

      {/* Banner */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Banner Image</label>
        <div className="relative h-32 rounded-xl overflow-hidden bg-[#f5f3ef] border border-[#e8e4de]">
          {form.banner_image ? <img src={form.banner_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><ImagePlus className="w-8 h-8" /></div>}
          <label className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity cursor-pointer rounded-xl">
            <span className="text-white text-xs font-medium">{uploadingBanner ? "Uploading..." : "Change Banner"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e, "banner_image", setUploadingBanner)} disabled={uploadingBanner} />
          </label>
        </div>
      </div>

      {/* Avatar */}
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#f5f3ef] border border-[#e8e4de] flex-shrink-0">
          {form.avatar ? <img src={form.avatar} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><UserCircle className="w-8 h-8 text-gray-300" /></div>}
        </div>
        <div>
          <p className="text-sm font-medium text-[#1a1a1a] mb-1">Profile Photo</p>
          <label className="cursor-pointer text-sm text-[#c8a96e] font-medium hover:underline">
            {uploadingAvatar ? "Uploading..." : "Upload Photo"}
            <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e, "avatar", setUploadingAvatar)} disabled={uploadingAvatar} />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Display Name *</label>
          <input required value={form.name} onChange={e => set("name", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Specialty</label>
          <input value={form.specialty || ""} onChange={e => set("specialty", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="e.g. Watercolor Art" />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Primary Category</label>
        <select value={form.primary_category || ""} onChange={e => set("primary_category", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c8a96e]">
          <option value="">Select your category...</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Bio *</label>
        <textarea required value={form.bio} onChange={e => set("bio", e.target.value)} rows={4} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e] resize-none" placeholder="Tell your story..." />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Location</label>
        <input value={form.location || ""} onChange={e => set("location", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="City, State" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Instagram</label>
          <input value={form.social_instagram || ""} onChange={e => set("social_instagram", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="@handle" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Twitter / X</label>
          <input value={form.social_twitter || ""} onChange={e => set("social_twitter", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="@handle" />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
          <Youtube className="w-3.5 h-3.5 text-red-500" /> YouTube Intro Video URL
        </label>
        <input type="url" value={form.youtube_video_url || ""} onChange={e => set("youtube_video_url", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="https://www.youtube.com/watch?v=..." />
        <p className="text-xs text-gray-400 mt-1">This video will appear as the hero on your public profile.</p>
      </div>

      <div className="flex gap-3 pt-2">
        <Link to={createPageUrl(`CreatorProfile?id=${creator.id}`)} target="_blank" className="inline-flex items-center gap-2 border border-[#e8e4de] text-[#1a1a1a] px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#f5f3ef] transition-colors">
          <Eye className="w-4 h-4" /> Preview Profile
        </Link>
        <button type="submit" disabled={saving} className="flex-1 bg-[#1a1a1a] hover:bg-[#c8a96e] text-white py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Profile"}
        </button>
      </div>
    </form>
  );
}

// ─── Apply / Register Flow ─────────────────────────────────────────────────
function ApplyForm({ user, onCreated }) {
  const [form, setForm] = useState({ name: user?.fullName || "", bio: "", specialty: "", primary_category: "", location: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    const creator = await Creator.create({ ...form, user_id: user.id, total_sales: 0, is_featured: false });
    setSaving(false); onCreated(creator);
  };

  return (
    <div className="max-w-lg mx-auto py-16 px-4">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#c8a96e]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-[#c8a96e]" />
        </div>
        <h2 className="font-display text-3xl font-bold text-[#1a1a1a] mb-2">Become a Creator</h2>
        <p className="text-gray-500 text-sm">Set up your creator profile to start showcasing your work, projects, and offerings.</p>
      </div>
      <form onSubmit={handleSubmit} className="bg-white border border-[#e8e4de] rounded-2xl p-6 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Display Name *</label>
          <input required value={form.name} onChange={e => set("name", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="Your name or studio name" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Primary Category *</label>
          <select required value={form.primary_category} onChange={e => set("primary_category", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c8a96e]">
            <option value="">Select your creator type...</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Specialty</label>
          <input value={form.specialty} onChange={e => set("specialty", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="e.g. Watercolor Art, Custom Jewelry" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Bio *</label>
          <textarea required value={form.bio} onChange={e => set("bio", e.target.value)} rows={3} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e] resize-none" placeholder="Tell your story in a few sentences..." />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Location</label>
          <input value={form.location} onChange={e => set("location", e.target.value)} className="w-full border border-[#e8e4de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#c8a96e]" placeholder="City, State" />
        </div>
        <button type="submit" disabled={saving} className="w-full bg-[#1a1a1a] hover:bg-[#c8a96e] text-white py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {saving ? "Creating Profile..." : "Create My Creator Profile"}
        </button>
      </form>
    </div>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────
export default function CreatorDashboard() {
  const [user, setUser] = useState(null);
  const [creator, setCreator] = useState(null);
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("products");
  const [productForm, setProductForm] = useState(null); // null=closed, false=new, obj=edit

  useEffect(() => {
    async function load() {
      const me = await User.me();
      setUser(me);
      if (!me) { setLoading(false); return; }
      // Find creator profile linked to this user
      const creators = await Creator.filter({ user_id: me.id }, "-createdAt", 1);
      if (creators.length > 0) {
        const c = creators[0];
        setCreator(c);
        const [prods, projs, offs] = await Promise.all([
          Product.filter({ creator_id: c.id }, "-createdAt", 50),
          Project.filter({ creator_id: c.id }, "-createdAt", 50),
          Offering.filter({ creator_id: c.id }, "-createdAt", 50),
        ]);
        setProducts(prods); setProjects(projs); setOfferings(offs);
      }
      setLoading(false);
    }
    load();
  }, []);

  const refreshProducts = async () => {
    const prods = await Product.filter({ creator_id: creator.id }, "-createdAt", 50);
    setProducts(prods); setProductForm(null);
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await Product.delete(id);
    setProducts(p => p.filter(x => x.id !== id));
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin w-8 h-8 border-4 border-[#c8a96e] border-t-transparent rounded-full" />
    </div>
  );

  if (!user) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
      <h2 className="font-display text-2xl font-bold mb-2">Sign In Required</h2>
      <p className="text-gray-500 text-sm mb-6">Please sign in to access your creator dashboard.</p>
      <Link to={createPageUrl("Home")} className="bg-[#1a1a1a] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#c8a96e] transition-colors">Back to Home</Link>
    </div>
  );

  // Not yet a creator — show apply form
  if (!creator) return <ApplyForm user={user} onCreated={(c) => { setCreator(c); }} />;

  const activeProducts = products.filter(p => p.status === "active").length;
  const completedProjects = projects.filter(p => p.status === "completed" || p.status === "featured").length;

  const statCards = [
    { label: "Total Products", value: products.length, icon: <Package className="w-5 h-5" />, color: "bg-blue-50 text-blue-600" },
    { label: "Active Listings", value: activeProducts, icon: <TrendingUp className="w-5 h-5" />, color: "bg-green-50 text-green-600" },
    { label: "Projects Done", value: completedProjects, icon: <Briefcase className="w-5 h-5" />, color: "bg-purple-50 text-purple-600" },
    { label: "Offerings", value: offerings.length, icon: <Sparkles className="w-5 h-5" />, color: "bg-amber-50 text-amber-600" },
    { label: "Total Sales", value: creator.total_sales || 0, icon: <TrendingUp className="w-5 h-5" />, color: "bg-rose-50 text-rose-600" },
    { label: "Rating", value: creator.rating ? `${creator.rating}★` : "—", icon: <Star className="w-5 h-5" />, color: "bg-yellow-50 text-yellow-600" },
  ];

  const tabs = [
    { id: "products", label: "Manage Products", icon: <Package className="w-4 h-4" /> },
    { id: "profile", label: "Manage Profile", icon: <UserCircle className="w-4 h-4" /> },
    { id: "inventory", label: "Upload Inventory", icon: <UploadCloud className="w-4 h-4" /> },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <LayoutDashboard className="w-5 h-5 text-[#c8a96e]" />
            <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest">Creator Dashboard</p>
          </div>
          <h1 className="font-display text-3xl font-bold text-[#1a1a1a]">Welcome back, {creator.name}</h1>
          <p className="text-gray-500 text-sm mt-1">{creator.primary_category || creator.specialty}</p>
        </div>
        <Link
          to={createPageUrl(`CreatorProfile?id=${creator.id}`)}
          className="hidden sm:inline-flex items-center gap-2 border border-[#e8e4de] hover:border-[#c8a96e] text-[#1a1a1a] hover:text-[#c8a96e] text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
        >
          <Eye className="w-4 h-4" /> View Public Profile
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        {statCards.map(s => (
          <div key={s.label} className="bg-white border border-[#e8e4de] rounded-xl p-4">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${s.color}`}>{s.icon}</div>
            <div className="font-display text-2xl font-bold text-[#1a1a1a]">{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-[#e8e4de] rounded-xl p-1 mb-8">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.id ? "bg-[#1a1a1a] text-white shadow-sm" : "text-gray-500 hover:text-[#1a1a1a]"
            }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── Tab: Manage Products ── */}
      {activeTab === "products" && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-semibold text-[#1a1a1a] text-lg">Your Products</h2>
            <button onClick={() => setProductForm(false)} className="inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#c8a96e] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
              <PlusCircle className="w-4 h-4" /> Add Product
            </button>
          </div>
          <div className="bg-white border border-[#e8e4de] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#faf9f7] border-b border-[#e8e4de]">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Product</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Price</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Stock</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e8e4de]">
                  {products.map(p => (
                    <tr key={p.id} className="hover:bg-[#faf9f7] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg overflow-hidden bg-[#f5f3ef] flex-shrink-0">
                            {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                          </div>
                          <p className="font-medium text-[#1a1a1a] line-clamp-1">{p.name}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{p.category}</td>
                      <td className="px-4 py-3 font-semibold">${p.price}</td>
                      <td className="px-4 py-3 text-gray-500">{p.stock}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.status === "active" ? "bg-green-100 text-green-600" : p.status === "draft" ? "bg-yellow-100 text-yellow-600" : "bg-red-100 text-red-500"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setProductForm(p)} className="p-1.5 text-gray-400 hover:text-[#c8a96e]"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deleteProduct(p.id)} className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">No products yet. Add your first one!</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {productForm !== null && (
            <ProductForm
              product={productForm || null}
              creatorId={creator.id}
              creatorName={creator.name}
              onSave={refreshProducts}
              onClose={() => setProductForm(null)}
            />
          )}
        </div>
      )}

      {/* ── Tab: Manage Profile ── */}
      {activeTab === "profile" && (
        <div>
          <h2 className="font-semibold text-[#1a1a1a] text-lg mb-5">Your Creator Profile</h2>
          <ProfileTab creator={creator} onSaved={(updated) => setCreator(c => ({ ...c, ...updated }))} />
        </div>
      )}

      {/* ── Tab: Upload Inventory ── */}
      {activeTab === "inventory" && (
        <div>
          <h2 className="font-semibold text-[#1a1a1a] text-lg mb-2">Upload Inventory</h2>
          <p className="text-gray-500 text-sm mb-6">Use the tool below to upload and manage your inventory.</p>
          <div className="bg-white border border-[#e8e4de] rounded-2xl overflow-hidden">
            <iframe
              src="https://crimson-bright-constellation.agentui.app?x_token_auth=eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJ3b3JrZmxvd0lkIjoiY21wano3ZTEwMHpqenMyMmJld3hkbXVodyIsImlzU2VydmljZVRva2VuIjp0cnVlLCJleHAiOjE4MTExNzY0MTcsImp0aSI6Ijc3MzI5M2U3ZTA2YTQ3NTUzNTFkOWM1NGNmNjZkZDA2IiwiaWF0IjoxNzc5NjQwNDE3fQ.MHgJ8ZETDb_qufSV2fgL3fYP4lIVjR5nuhJJzxotttCDvFDK9wGEACQQ9OD1GsYSXsm0mWGBmwgJOiFKPDv9hoobbec7AZXuchSugAQp8JcwcaYZAlka8q_q8PAcy_YI0Kq1frxUVkt8YlM0CMgAGRizaL1EOdJe24qJr8QX-O1c6EP_b7jKnimCB5WrURUWincF8p9Nbl86aCLdYC2YmNsy8YEMqQQ8xfl1nJGDjHCWTFD1e-f4GlbBHlbdKp3VrQ2mYQjpY55JbgMjQRBofiOoyxeVlkubMYf0FDiTH-QAhsVqTMGQOqE3P-JFT5hjfCUzBUrZTjWQcWxcOTjq2w&embed=true"
              width="100%"
              height="600"
              frameBorder="0"
              allowFullScreen
              className="block"
              title="Inventory Upload Tool"
            />
          </div>
        </div>
      )}
    </div>
  );
}